import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { alpha, Box, Chip, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import { DealLineChange, NewBusinessKind, useDealNewBusiness } from 'api/aws/deals/useDealNewBusiness'
import { useMemo } from 'react'

interface ProductFields {
  productName?: string
  price?: number | string
  startDate?: string
  endDate?: string
}

interface DealChangeSummaryProps {
  PK?: string
  dealUuid?: string
  productRows: ProductFields[]
}

const formatUSD = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 })

const formatSigned = (n: number): string => {
  if (n === 0) return '$0'
  return `${n > 0 ? '+' : '−'}${formatUSD(Math.abs(n))}`
}

const KIND_COLOR: Record<NewBusinessKind, 'primary' | 'secondary' | 'success' | 'default'> = {
  new_logo: 'primary',
  cross_sell: 'secondary',
  upsell: 'success',
  cross_sell_and_upsell: 'success',
  renewal: 'default',
}

const lineLabel = (line: DealLineChange, isNewLogo: boolean): string => {
  if (line.change === 'new') return isNewLogo ? 'New' : 'Cross-sell'
  if (line.change === 'increase') return 'Increase'
  if (line.change === 'decrease') return 'Decrease'
  return 'No change'
}

const LineIcon = ({ change }: { change: DealLineChange['change'] }) => {
  const sx = { fontSize: 18, flexShrink: 0 }
  if (change === 'new') return <AddCircleOutlineIcon sx={{ ...sx, color: 'primary.main' }} />
  if (change === 'increase') return <TrendingUpIcon sx={{ ...sx, color: 'success.main' }} />
  if (change === 'decrease') return <TrendingDownIcon sx={{ ...sx, color: 'error.main' }} />
  return <DragHandleIcon sx={{ ...sx, color: 'text.secondary' }} />
}

/**
 * New logo, cross-sell, upsell or renewal for the deal under review, with each
 * service line's increase or decrease against the most the org has billed on
 * it. The same comparison #tf-newbusiness announces when the deal is won.
 */
const DealChangeSummary = ({ PK, dealUuid, productRows }: DealChangeSummaryProps) => {
  const theme = useTheme()
  const productSignature = useMemo(
    () =>
      productRows
        .map(p => `${p?.productName ?? ''}:${p?.price ?? ''}:${p?.startDate ?? ''}:${p?.endDate ?? ''}`)
        .sort()
        .join('|'),
    [productRows]
  )
  const { data, isLoading, isError } = useDealNewBusiness(PK, dealUuid, productSignature)

  if (isLoading) {
    return <Skeleton variant="rounded" height={96} sx={{ borderRadius: 3 }} />
  }
  if (isError || !data) {
    return (
      <Typography sx={{ fontSize: 12, color: 'text.disabled', px: 1 }}>
        Couldn't work out what this deal adds over the account's current billing.
      </Typography>
    )
  }

  const isNewLogo = data.kind === 'new_logo'

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          What this deal adds
        </Typography>
        <Chip size="small" label={data.label} color={KIND_COLOR[data.kind]} variant="outlined" />
        <Typography variant="body2" sx={{ color: data.newMrr > 0 ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
          {data.newMrr > 0 ? `${formatSigned(data.newMrr)}/mo new MRR` : 'No new MRR'}
        </Typography>
        <Tooltip title="Each line is compared with the most this account has billed on it in any earlier won deal. This is the same comparison the #tf-newbusiness post uses.">
          <Typography variant="caption" sx={{ color: 'text.disabled', ml: { sm: 'auto' }, cursor: 'help' }}>
            vs. prior high per service line
          </Typography>
        </Tooltip>
      </Box>

      {data.lines.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 1.5,
          }}
        >
          {data.lines.map(line => (
            <Box
              key={line.line}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.25,
                minWidth: 0,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <LineIcon change={line.change} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {lineLabel(line, isNewLogo)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', flexShrink: 0 }}>
                  {line.line.replace(/_/g, ' ')}
                </Typography>
              </Box>
              <Tooltip title={line.products.join(', ')}>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {line.products.join(', ') || '—'}
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {line.before === null ? 'Not billed before' : formatUSD(line.before)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  →
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatUSD(line.after)}
                </Typography>
                {line.change !== 'same' && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      ml: 'auto',
                      color: line.delta > 0 ? 'success.main' : line.delta < 0 ? 'error.main' : 'text.secondary',
                    }}
                  >
                    {formatSigned(line.delta)}/mo
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default DealChangeSummary
